/**
 * DiscountRulesPage — `/settings/discount-rules`.
 *
 * Volume / promo / corporate / seasonal discount rules. Backed by the
 * generic settings-collection store on service-audit
 * (`GET / PUT /v1/settings-collections/discount_rules`).
 */
import { type JSX, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Percent, Plus, Save, Trash2 } from 'lucide-react';

import {
  getSettingsCollection,
  putSettingsCollection,
} from '../../lib/api/settings';
import { PageHeader } from '../shared/StaffUI';

interface Rule {
  readonly id: string;
  readonly name: string;
  readonly type: 'VOLUME' | 'PROMO' | 'CORPORATE' | 'SEASONAL';
  readonly percent: number;
  readonly threshold: number;
}

const COLLECTION_KEY = 'discount_rules' as const;

export function DiscountRulesPage(): JSX.Element {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['settings-collection', COLLECTION_KEY],
    queryFn: () => getSettingsCollection<Rule>(COLLECTION_KEY),
    retry: false,
    staleTime: 30_000,
  });

  const [rules, setRules] = useState<readonly Rule[]>([]);
  const [dirty, setDirty] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState<Rule['type']>('VOLUME');
  const [percent, setPercent] = useState('');
  const [threshold, setThreshold] = useState('');

  useEffect(() => {
    if (query.data && !dirty) {
      setRules(query.data.items);
    }
  }, [query.data, dirty]);

  const saveMut = useMutation({
    mutationFn: (next: readonly Rule[]) =>
      putSettingsCollection<Rule>(COLLECTION_KEY, next),
    onSuccess: async () => {
      setDirty(false);
      await qc.invalidateQueries({ queryKey: ['settings-collection', COLLECTION_KEY] });
    },
  });

  function add(): void {
    if (!name.trim() || !percent) return;
    const next: readonly Rule[] = [
      ...rules,
      {
        id: `d-${Date.now()}`,
        name: name.trim(),
        type,
        percent: Number(percent),
        threshold: Number(threshold) || 0,
      },
    ];
    setRules(next);
    setDirty(true);
    setName('');
    setPercent('');
    setThreshold('');
  }

  function remove(id: string): void {
    setRules(rules.filter((r) => r.id !== id));
    setDirty(true);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Discount rules"
        description="Volume, promotional, corporate and seasonal discounts."
        actions={
          dirty ? (
            <button
              type="button"
              onClick={() => saveMut.mutate(rules)}
              disabled={saveMut.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-nipost-green text-white rounded-lg text-sm font-medium disabled:opacity-60"
            >
              <Save className="w-4 h-4" /> {saveMut.isPending ? 'Saving…' : 'Save changes'}
            </button>
          ) : null
        }
      />

      {query.isPending ? (
        <p className="text-sm text-muted">Loading discount rules…</p>
      ) : query.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Could not load discount rules: {(query.error as Error).message}
        </div>
      ) : null}

      {saveMut.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Save failed: {(saveMut.error as Error).message}
        </div>
      ) : null}

      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Percent className="w-4 h-4 text-nipost-green" />
          <h3 className="font-semibold text-gray-900">Add rule</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Rule name"
            aria-label="Rule name"
            className="px-3 py-2 text-sm border border-border rounded-lg"
          />
          <select
            value={type}
            onChange={(e) => setType(e.target.value as Rule['type'])}
            aria-label="Rule type"
            className="px-3 py-2 text-sm border border-border rounded-lg"
          >
            <option value="VOLUME">Volume</option>
            <option value="PROMO">Promo</option>
            <option value="CORPORATE">Corporate</option>
            <option value="SEASONAL">Seasonal</option>
          </select>
          <input
            type="number"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            placeholder="Discount %"
            aria-label="Percent"
            className="px-3 py-2 text-sm border border-border rounded-lg"
          />
          <input
            type="number"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            placeholder="Threshold"
            aria-label="Threshold"
            className="px-3 py-2 text-sm border border-border rounded-lg"
          />
        </div>
        <button
          type="button"
          onClick={add}
          className="mt-3 inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg"
        >
          <Plus className="w-3.5 h-3.5" /> Add rule
        </button>
      </div>

      <div className="bg-white border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
            <tr>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Type</th>
              <th className="text-right px-4 py-2">Discount</th>
              <th className="text-right px-4 py-2">Threshold</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rules.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-muted">
                  No rules configured.
                </td>
              </tr>
            ) : (
              rules.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-900 font-medium">{r.name}</td>
                  <td className="px-4 py-2 text-xs">{r.type}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.percent}%</td>
                  <td className="px-4 py-2 text-right font-mono">{r.threshold}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => remove(r.id)}
                      aria-label={`Remove ${r.name}`}
                      className="text-red-600 hover:bg-red-50 rounded p-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
